import { ACTIONS, Step } from 'react-joyride';
import { EventBus } from '../app/contexts/event-bus';
import { LayersAircraftNavState, LayersNavState } from '../app/features/home/flyouts/LayersFlyout/LayersFlyout.types';
import { HomeFlyoutTypes } from '../app/features/home/flyouts/types';
import { SETTINGS_ROUTES } from '../app/features/settings/router/settings-routes';
import { ROUTES } from '../app/router/routes';
import { setTutorialSelectedFlight } from '../app/state/slices/workspace-slice';
import { store } from '../app/state/store';

export type FeStep = Omit<Step, 'data'> & {
    hideBackButton?: boolean;
    data?: {
        bottom?: string;
        hideNextButton?: boolean;
        autoClick?: boolean;
        canGoNext?: (this: FeStep) => Promise<boolean>;
        requiresCustomerAdmin?: boolean;
        afterNavigate?: string;
    };
};

const home: FeStep[] = [
    {
        target: '#main-left-nav',
        title: 'Main menu',
        content: 'The left side menu allows you to choose from major Flight Explorer functions.',
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#search-btn',
        title: 'Search icon',
        content:
            'The Search icon allows searching for any flight.  You can then go to the flight on the map, display current & last known location details and create an aircraft filter from your search entry (including wildcards).',
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#map-vlwa-nav',
        title: 'Map controls',
        content:
            'Views, Layers, Weather and Aircraft filters icons allow the map to be customized for your particular use, and saved for future use.',
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#status-bar',
        title: 'Status bar',
        content:
            'The status bar shows the current connection state, My Fleet, current View selected, the active plane count and current and last update times.',
        placement: 'top',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#main-layout-map-tools',
        title: 'Map tools',
        content: 'Map tools for latitude/longitude & distance/bearing can be accessed via these buttons.',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#main-constraints',
        title: 'Constraints list',
        content:
            'The top toolbar includes new buttons to display the constraints list dialog and also to show constraints graphically on the map.',
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#map-0',
        title: 'Diversion Manager',
        content: (
            <div>
                Diversion Manager provides the ability to monitor diversion capacity at designated airports and generate alerts when active
                diversions approach capacity. For each airport, diversion and holding statistics can be displayed, as well as alert
                thresholds set.
                <br />
                <br />
                <em>Right click an airport, select Diversion/Holding status.</em>
            </div>
        ),
        placement: 'bottom',
        hideBackButton: true,
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#nav-toolbar-icons',
        title: 'Title bar icons',
        content: 'Title bar icons allow access to tutorials, light and dark themes and the user drop down menu.',
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#account-btn',
        title: 'User dropdown menu',
        content: 'User dropdown menu allows access to full screen mode, feedback, slide show mode and logout.',
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#map-0',
        title: 'Map navigation',
        content:
            'The map can be panned by left click drag.  It can be zoomed with the mouse wheel.  It can be rotated and tilted (with certain basemaps) by holding down control and left click dragging the mouse, and can be reset to regular orientation by clicking the up/down arrow icon in the lower left corner of the map.  The associated down arrow will expose rotate and tilt sliders for more accurate control.',
        skipBeacon: true,
        blockTargetInteraction: false
    }
];

const createUdView: FeStep[] = [
    {
        target: '#views-btn',
        data: {
            autoClick: true
        },
        title: 'Create View',
        content: (
            <div>
                Views are used to save the current map layers, weather layers, aircraft filters, geographical area and all associated
                settings, for future use.
                <br />
                <br />
                <em>Click the Views icon</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true
    },
    {
        target: '#add-view-btn',
        data: {
            autoClick: true
        },
        before: delayFunc(250),
        content: (
            <div>
                Press the plus sign
                <br />
                <br />
                <em>Click the plus sign</em>
            </div>
        ),
        hideBackButton: true,
        title: 'Create View',
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#udViewName',
        content: 'Enter a name for the new View',
        title: 'Create View',
        hideBackButton: true,
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#viewType',
        title: 'Create View',
        content: (
            <div>
                Flight Explorer supports both User Views and Company Views. User Views are private for each user. Company Views are shared
                by all users in a company. Only authorized users can create, update and delete Company Views.
                <br />
                <br />
                <em>Choose whether this View is a User view or a Company View.</em>
            </div>
        ),
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#includeInSlideshow',
        title: 'Create View',
        content: (
            <div>
                Flight Explorer supports slide show mode, where various Views can be cycled between continuously on the map. This is mainly
                used for large screen display or dashboards.
                <br />
                <br />
                <em>Choose whether this View should be part of slide show mode.</em>
            </div>
        ),
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#viewSave',
        title: 'Create View',
        content: 'Press save',
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#views-flyout .default',
        title: 'Select previously saved View',
        content: (
            <div>
                Selecting a previously saved View is as simple as selecting it's name from the view list.
                <br />
                <br />
                <em>Select Default View</em>
            </div>
        ),
        hideBackButton: true,
        blockTargetInteraction: false,
        placement: 'auto',
        skipBeacon: true,
        data: {
            autoClick: true
        }
    },
    {
        target: '#views-flyout .last-saved',
        title: 'Select previously saved View',
        content: 'Select new view you just created',
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#view-save-btn',
        title: 'Update View',
        content: (
            <div>
                Once you have selected a View, you can change any of the aircraft filters, layers or settings, and then save those updated
                settings to the existing View.
                <br />
                <br />
                <em>Click Save current View icon</em>
            </div>
        ),
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#views-flyout .last-saved .view-edit-btn',
        title: 'Edit View name',
        content: 'You can edit any View name using the pencil icon.',
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#views-flyout .last-saved .view-delete-btn',
        title: 'Delete View',
        content: 'You can delete a View using the trash can icon.',
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#views-flyout .last-saved .view-fav-btn',
        title: 'Mark View as favorite',
        content:
            'You can mark a View as a favorite using the favorite icon.  The View will them appear at the top of the View list for easy selection.',
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false
    }
] as const;

const myFleetAndFilter: FeStep[] = [
    {
        target: '#nav-settings',
        title: 'Define My Fleet',
        content: (
            <div>
                You can optionally define a set of aircraft as "My Fleet." This will make it easier to show/filter those aircraft that are
                most important to you.
                <br />
                <br />
                <em>Click Settings icon.</em>
            </div>
        ),
        placement: 'top',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: 'a[href="/settings/aircraft"]',
        title: 'Define My Fleet',
        content: `Click Aircraft category`,
        targetWaitTimeout: 5000,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#my-fleet-mode',
        title: 'Define My Fleet',
        skipScroll: true,
        content: (
            <div>
                My Fleet can be set to Airline or Airport mode. Airline mode allows you to set My Fleet to one or more 3-letter airline IDs.
                Airport mode allows you to set My Fleet to all flight arriving and/or departing from a specific airport.
                <br />
                <br />
                <em>Select "Airline" My Fleet mode.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        skipScroll: true,
        target: '#my-fleet-airline-list',
        title: 'Define My Fleet',
        content: `Enter 3-letter airline ID and press ENTER or ',' to add to My Fleet.`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#nav-home',
        title: 'Define My Fleet',
        content: `Return to map by clicking Home icon.`,
        placement: 'auto',
        hideBackButton: true,
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#filter-btn',
        title: 'Create an aircraft filter',
        hideBackButton: true,
        content: (
            <div>
                The Flight Explorer map shows all aircraft being tracked unless one or more filters are defined to limit the display.
                <br />
                <br />
                <em>Click Filter icon.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#filter-add-btn',
        title: 'Create an aircraft filter',
        hideBackButton: true,
        before: delayFunc(350),
        content: `Click plus sign to create a new aircraft filter`,
        placement: 'auto',
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#filter-name',
        hideBackButton: true,
        title: 'Create an aircraft filter',
        content: `Enter filter name. If you don't enter a name, one will be created for you based on the filter criteria you enter.`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-color',
        title: 'Create an aircraft filter',
        content: `Set color for these filtered aircraft or leave as default`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-my-fleet-select',
        title: 'Create an aircraft filter',
        content: `Choose whether this filter should include the My Fleet flights you previously defined.`,
        placement: 'top',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-acid',
        title: 'Create an aircraft filter',
        content: `If not including My Fleet, set a callsign with optional wildcard (ie. ABC*), origin, dest or any other field to uniquely identify the aircraft you want to include in this filter.`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-origin',
        title: 'Create an aircraft filter',
        content: `If not including My Fleet, set a callsign with optional wildcard (ie. ABC*), origin, dest or any other field to uniquely identify the aircraft you want to include in this filter.`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-destination',
        title: 'Create an aircraft filter',
        content: `If not including My Fleet, set a callsign with optional wildcard (ie. ABC*), origin, dest or any other field to uniquely identify the aircraft you want to include in this filter.`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-show-tags',
        title: 'Create an aircraft filter',
        content: `Choose whether to show tags for these flights. Filter tags only show when the map is zoomed in beyond the level set under aircraft layer settings.`,
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#filter-save',
        title: 'Create an aircraft filter',
        content: `Save new filter. Make sure you have entered at least one filter criteria above or the Save button will not be active.`,
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true,
            canGoNext: function () {
                (document.querySelector('#filters-validate-button') as HTMLButtonElement)?.click();
                return new Promise<boolean>((resolve) => {
                    self.setTimeout(() => {
                        resolve(document.querySelector('#filters-save-error-msg') == null);
                    }, 100);
                });
            }
        }
    },
    {
        target: '#filter-add-btn',
        title: 'Add more filters',
        content: `You can optionally add more filters to show additional aircraft.`,
        hideBackButton: true,
        placement: 'auto',
        blockTargetInteraction: true,
        data: {
            autoClick: false
        }
    },
    {
        target: '#filters-apply-filters',
        title: 'Enable/disable all filters',
        content: `Toggle the Apply filters setting to switch between all aircraft or filtered aircraft.`,
        placement: 'auto',
        blockTargetInteraction: false
    },
    {
        target: '#filter-list input[type="checkbox"]',
        title: 'Enable/disable individual filters',
        content: `Each filter can be enabled/disabled individually.`,
        placement: 'auto',
        blockTargetInteraction: false
    },
    {
        target: '#filter-list .filter-edit-btn',
        title: 'Edit filter',
        content: `The pencil icon can be used to edit previously created filters.`,
        placement: 'auto',
        blockTargetInteraction: true
    },
    {
        target: '#filter-list .filter-delete-btn',
        title: 'Delete filter',
        content: `The trash can icon can be used to delete a filter.`,
        placement: 'auto',
        blockTargetInteraction: true
    },
    {
        target: '#filter-list .filter-reorder-btn',
        title: 'Reorder filters',
        content: `Filters are applied from the top down.  The reorder icon can be used to click and drag filters`,
        placement: 'auto',
        blockTargetInteraction: true
    },
    {
        target: '#filters-flyout',
        title: 'Save filters',
        content: `Filters will be lost when you shut down Flight Explorer unless they are saved.  Filters are saved as part of a View.  Run the tutorial "Create, update and select Views" to learn how to save your aircraft filters and other map layer selections.`,
        placement: 'auto',
        blockTargetInteraction: true
    }
] as const;

const weather: FeStep[] = [
    {
        target: '#weather-btn',
        title: 'Select weather layers',
        content: (
            <div>
                You can select multiple weather products for display on the map.
                <br />
                <br />
                <em>Click weather icon</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#weather-products-container .weather-category-item:nth-child(2) .weather-category-item-header',
        title: 'Select weather layers',
        content: (
            <div>
                Weather products are organized into categories.
                <br />
                <br />
                <em>Select the Radar category.</em>
            </div>
        ),
        placement: 'auto',
        before: delayFunc(300),
        skipBeacon: true,
        blockTargetInteraction: false,
        hideBackButton: true,
        data: {
            autoClick: true
        }
    },
    {
        skipScroll: true,
        target: '#weather-products-container .product-item',
        title: 'Select weather layers',
        content: 'Choose one or more weather products for display by enabling in list.',
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        skipScroll: true,
        target: '#weather-products-container .product-item .weather-favorite-btn',
        title: 'Select weather layers',
        content: (
            <div>
                You can set commonly used weather products as favorites so they appear in the favorites section.
                <br />
                <br />
                <em>Choose one or more products as favorites.</em>
            </div>
        ),
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        skipScroll: true,
        target: '#weather-products-container',
        title: 'Select weather layers',
        content: (
            <div>
                Your weather selections can be saved as part of the View.
                <br />
                <em>See the "Create, update and select Views" tutorial.</em>
            </div>
        ),
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: true
    }
] as const;

const layers: FeStep[] = [
    {
        target: '#layers-btn',
        title: 'Select map layers',
        content: (
            <div>
                You can enable one or more map layers and customize associated settings for each.
                <br />
                <br />
                <em>Click Layers icon</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#layers-map-background',
        title: 'Select map layers',
        before: delayFunc(350),
        content: (
            <div>
                You can choose from multiple background map options.
                <br />
                <br />
                <em>Select a background map by clicking on the background map dropdown.</em>
            </div>
        ),
        hideBackButton: true,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#layers-projection',
        title: 'Select map layers',
        content: (
            <div>
                By default, Mercator projection is always used, regardless of zoom level. You can choose, for a particular View, to enable
                the globe projection when zoomed out. This allows better visualization of longer flight routes.
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#layers-day-night',
        content: (
            <div>
                Flight Explorer supports basic day/night shading and advanced twilight delineation.
                <br />
                <br />
                <em>Enable day/night shading and optionally choose the type of shading with the right arrow.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#layers-list',
        content: (
            <div>
                Flight Explorer supports advanced aviation and navigation layers. Each layer has a set of sub-categories that can be enabled
                individually to customize the map. Filter groups can also be defined for further customization. All layer selections are in
                this menu, except weather, which you use the dedicated weather button to access.
                <br />
                <br />
                <em>Enable one or more layers using the corresponding checkboxes.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '.section-expand-btn',
        title: 'Select map layers',
        content: (
            <div>
                Some layers have additional settings available. The aircraft layer has options for user defined tags, icon size, range
                rings, velocity vectors and coast mode and more.
                <br />
                <br />
                <em>Click the aircraft layer right arrow to access additional aircraft settings.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#layers-flyout',
        title: 'Select map layers',
        content: `Your layer selections can be saved as part of the View.  See the tutorial "Create, update and select Views"`,
        placement: 'left',
        skipBeacon: true,
        blockTargetInteraction: false
    }
] as const;

const flightDetails: FeStep[] = [
    {
        target: '#map-0',
        title: 'Basic aircraft details',
        content: (
            <div>
                You can hover over any aircraft to view basic details. Additional fields can be added to the tool tip under the Settings
                menu.
                <br />
                <br />
                <em>Hover over an aircraft</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true
    },
    {
        target: '#map-0',
        title: 'Full aircraft details',
        content: (
            <div>
                You can select an aircraft to View full flight details.
                <br />
                <br />
                <em>Left click on an aircraft. Multiple aircraft can be selected using shift-left click and unselected the same way.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true
    },
    {
        target: '#flight-details-flyout',
        title: 'Aircraft details',
        content:
            'The aircraft details dialog panel opens on the left side of the map.  Here you can see full details about the aircraft and select additional aircraft information to show on the map.',
        placement: 'auto',
        skipBeacon: true,
        before: () => {
            store.dispatch(setTutorialSelectedFlight());
            return delay(100);
        }
    },
    {
        target: '#flight-details-flyout .flyout-header',
        title: 'Aircraft details',
        content:
            'The title bar shows the aircraft ID, aircraft type and includes icons to close or minimize the window, pin/watch this flight and access additional tools (vertical ellipsis) like Emergency Diversion.  Once an aircraft is pinned, it can also be selected for the Follow Me feature which will keep the map centered on that aircraft.',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#flight-details-flyout .k-tabstrip-items-wrapper',
        title: 'Aircraft details',
        content:
            'The tab bar allows you to choose between the Summary page, History page and Impacts page.  The History page shows all previous position reports for a flight, while the Impacts page shows all Proactive Route Analytics impacts detected for this flight, even if you are not using them for alerting and monitoring.',
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#flight-summary-header',
        title: 'Aircraft details',
        content:
            "The flight line section shows details about the aircraft's current location between origin and destination, as well as departure time, ETA, current altitude and speed, and origin and destination airport IDs, which can be clicked to show the Airport Summary for each.",
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#flight-details-flight-information',
        title: 'Aircraft details',
        content: (
            <div>
                The flight information section shows basic flight details. It also allows you to select whether to automatically show
                profile and tag when an aircraft is selected, and whether to 'focus' on that one aircraft and/or zoom the display to
                'encompass' the entire flight route.
                <br></br>
                <em>Click the profile button and the tag button in the section header bar.</em>
            </div>
        ),
        blockTargetInteraction: true,
        skipBeacon: true
    },
    {
        target: '#flight-details-flight-position',
        title: 'Aircraft details',
        content: (
            <div>
                The position information section shows details about the aircraft's current location. It also allows you to select whether
                the route flown, points and labels should be shown on the map.
                <br></br>
                <em>Click any icon in the section header bar.</em>
            </div>
        ),
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#flight-details-flight-routes',
        title: 'Aircraft details',
        content: (
            <div>
                The route information section shows planned and ATC routes as well as other flight plan details. It also allows you to
                select whether the planned routes, points and labels should be displayed on the map.
                <br></br>
                <em>Click any icon in the section header bar.</em>
            </div>
        ),
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#flight-details-flight-registration-header',
        title: 'Aircraft details',
        content: (
            <div>
                For aircraft with a US registration # (tail #), an additional "Registration information" section will appear at the bottom
                of aircraft details. By clicking the down arrow to expand it, you can view the registration information for the aircraft.
                <br></br>
                <em>Click the down arrow in the section header bar.</em>
            </div>
        ),
        skipBeacon: true,
        blockTargetInteraction: true
    },
    {
        target: '#flight-details-profile-btn',
        title: 'Profile view',
        content: `Flight profile view is now available.  Please see the "View aircraft details" tutorial for more information.`,
        skipBeacon: true
    },
    {
        target: '#flight-summary-pin-btn',
        title: 'Pinned flights',
        content: (
            <div>
                Flight details strips can be pinned to the map to follow individual flights. Once pinned, the full aircraft details can
                still be viewed by clicking on the pinned flight strip.
                <br />
                <br />
                <em>Click the pin icon in the Aircraft details title bar.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#flight-details-notes-tab',
        title: 'My Notes',
        content: (
            <div>
                Notes can be created for both flights and airports. Both user and company notes are supported. These can be accessed from
                the new Flight Details Notes tab or from aircraft and airport context menus.
            </div>
        ),
        placement: 'auto',
        hideBackButton: true,
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#flight-details-etops-edto',
        title: 'ETOPs display',
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: true,
        content: <div>You can now show graphical ETOPS for flights that have 633 flights plans on both map and profile.</div>
    }
] as const;

const monitoringAlerting: FeStep[] = [
    {
        target: '#nav-settings',
        title: 'My Fleet',
        content: (
            <div>
                You have to define a set of aircraft as "My Fleet." Those will be the aircraft observed by the threshold system.
                <br />
                <br />
                <em>Click Settings icon.</em>
            </div>
        ),
        placement: 'top',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        targetWaitTimeout: 3000,
        target: 'a[href="/settings/aircraft"]',
        title: 'My Fleet',
        content: `Click Aircraft category`,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        skipScroll: true,
        target: '#settings-my-fleet',
        title: 'My Fleet',
        content: 'You must first set My Fleet to the group of aircraft that you are interested in monitoring and alerting on.',
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true
    },
    {
        target: 'a[href="/settings/monitor-alert"]',
        title: 'Aircraft thresholds',
        content: 'Click Monitor & Alert.',
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true,
        data: {
            autoClick: true
        }
    },
    {
        target: '#aircraft-thresholds-link',
        title: 'Aircraft thresholds',
        content:
            'Thresholds define the criteria by which aircraft change monitor status and generate alerts.  You can accept the defaults, or customize each threshold. Both User and Company thresholds are available. Editing Company thresholds requires additional account permissions.',
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true,
        data: {
            autoClick: true
        }
    },
    {
        skipScroll: true,
        target: '#aircraft-thresholds-first-row',
        title: 'Aircraft thresholds',
        content:
            'Each threshold can be enabled, assigned a relative priority, values chosen for up to five levels (Info through Emergency), and one or more alert methods can be chosen for notifications about changes in level for this the threshold.',
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true,
        targetWaitTimeout: 5000,
        after: () => {
            (document.querySelector('#nav-home') as HTMLButtonElement)?.click();
        }
    },
    {
        target: '#monitor-panels',
        title: 'Aircraft thresholds',
        content: 'The right menu bar allows access to Fleet Monitor, Airport Monitor and the Event List.',
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true,
        placement: 'left',
        after: () => {
            (document.querySelector('#home-fleet-monitor:not(.active)') as HTMLElement)?.click();
            (document.querySelector('#home-airport-monitor:not(.active)') as HTMLElement)?.click();
            (document.querySelector('#home-event-list:not(.active)') as HTMLElement)?.click();
        }
    },
    {
        target: '#fleet-monitor',
        title: 'Aircraft thresholds',
        before: delayFunc(300),
        content:
            'Fleet Monitor shows the status of all flights in My Fleet.  Each tag provides extensive details on each flight, including monitor status stripe, location, origin, destination, ACType, reg #, holding, diversion, OOOI times, and much more.  Right clicking on the tag will show a list of all thresholds with actual and breached values.',
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'left'
    },
    {
        target: '#airport-monitor',
        title: 'Aircraft thresholds',
        content:
            'Airport Monitor shows the status of all airports used by flights in My Fleet.  Each tag provides extensive details on each airport, including monitor status stripe, IDs, name and usage counts.  Right clicking on the tag will show a list of all thresholds with actual and breached values.',
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'left'
    },
    {
        target: '#event-list',
        title: 'Aircraft thresholds',
        content:
            'The Event List is one of the alert mechanisms that can be selected for each aircraft or airport threshold.  It is a scrolling list, indicating the changes in monitor status level for selected thresholds along with the details that caused the change.',
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'left'
    },
    {
        target: '#main-left-nav',
        title: 'Aircraft thresholds',
        content: 'The Flight List and Airport List have new columns that show the current Monitor Status color for aircraft and airports.',
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'right'
    }
];

const routeAnalyzer: Array<FeStep> = [
    {
        target: '#home-route-analyzer',
        title: 'Route analyzer',
        content: (
            <div>
                Route Analyzer allows you to compare multiple routes from various sources along with air traffic, weather and other airspace
                constraints. These routes can be graphically edited. You can access Route Analyzer via the right menu bar option or by right
                clicking on any aircraft and selecting Analyze routes. Route analyze can be closed by clicking the right menu bar option
                again.
                <br />
                <br />
                <em>Click Route analyzer in right menu bar.</em>
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'left',
        before: () => {
            (document.querySelector('#home-route-analyzer.active') as HTMLElement)?.click();
            return delay(100);
        },
        data: {
            autoClick: true
        }
    },
    {
        target: '#add-route-btn > button:nth-child(2)',
        title: 'Route analyzer',
        before: delayFunc(300),
        content: (
            <div>
                Routes can be entered ad-hoc or retrieved from CDR/Pref route database, company route CSV file, or from flight planning
                integration (Aug. release). Ad-hoc is the default "+" button action, but any of the other options can be chosen as the
                default action by clicking the star next to it in the dropdown.
                <br />
                <br />
                <em>Click down arrow</em>
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'top',
        data: {
            autoClick: true
        }
    },
    {
        target: '#add-route-btn-1',
        title: 'Route analyzer',
        content: <div>Click database</div>,
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'right',
        before: delayFunc(200),
        data: {
            autoClick: true
        }
    },
    {
        target: '#route-analyzer-database-import',
        title: 'Route analyzer',
        before: delayFunc(250),
        content: <div>Enter origin and destination airport IDs</div>,
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true
    },
    {
        target: '#route-analyzer-database-import-btn',
        title: 'Route analyzer',
        content: <div>Press import</div>,
        blockTargetInteraction: false,
        skipBeacon: true,
        data: {
            autoClick: true
        }
    },
    {
        skipScroll: true,
        target: '#route-analyzer-list .route-action-section',
        title: 'Route analyzer',
        content: (
            <div>
                Each route in the list can be deleted or edited to modify texturally or assign unique color using the icons at the right of
                each row.
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true
    },
    {
        skipScroll: true,
        target: '#route-analyzer-list .route-checkbox',
        title: 'Route analyzer',
        content: (
            <div>
                One or more routes can be drawn on the map and compared.
                <br />
                <br />
                <em>Enable checkbox for a route in the list.</em>
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true
    },
    {
        target: '#map-0',
        title: 'Route analyzer',
        content: (
            <div>
                The routes can be edited graphically on the map.
                <br />
                <br />
                <em>Right click on any route segment or waypoint and choose Edit graphically.</em>
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true
    },
    {
        target: '#map-0',
        title: 'Route analyzer',
        placement: 'bottom',
        content: (
            <div>
                Each waypoint can be dragged and snapped to any displayed NAVAIDs or fixes. Holding Alt will eliminate the snap and allow
                freeform lat/lon points. New points can be added by clicking the small dot between waypoints. Existing waypoints can be
                deleted by left clicking and pressing Del. Press ESC to cancel any changes or ENTER to save changes.
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true
    }
];

const drawingObjects: Array<FeStep> = [
    {
        target: '#drawing-objects-tools-btn',
        title: 'Drawing Objects',
        content: (
            <div>
                Drawing Objects can be used to depict areas on screen, for alerting on aircraft entry & exit, and for avoidance areas when
                requesting routes from flight planning (8/25 release). Circle/ellipse, rectangle & polygon shapes are supported. A list of
                all drawing object can be opened by choosing Drawing Object list.
                <br />
                <br />
                <em>Click the pencil icon in the top toolbar.</em>
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true,
        hideBackButton: true,
        placement: 'bottom',
        data: {
            autoClick: true
        }
    },
    {
        target: '#drawing-objects-tools-polygon',
        title: 'Drawing Objects',
        before: delayFunc(250),
        content: (
            <div>
                Select the shape type you want to create.
                <br />
                <br />
                <em>Click polygon</em>
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true,
        placement: 'right',
        hideBackButton: true,
        data: {
            autoClick: true
        }
    },
    {
        target: '#map-0',
        title: 'Drawing Objects',
        content: (
            <div>
                Click points on the map to create the polygon. For circles, click the center point, release and drag to size for ellipse,
                holding shift for circle. For rectangles, click one corner, release and drag to size. When finished, press ENTER to save or
                ESC to cancel.
                <br />
                <br />
                <em>To continue tutorial, draw an object on the map and press SPACE.</em>
            </div>
        ),
        blockTargetInteraction: false,
        hideBackButton: true,
        skipBeacon: true,
        data: {
            hideNextButton: true
        }
    },
    {
        target: '#drawing-objects-edit-window',
        placement: 'left',
        content: (
            <div>
                The drawing object properties dialog will open, and allow you to enter a name for the object as well as customize the colors
                and drawing styles. The object category can be set to be View based, Alert area or Avoidance area, and entry/exit alerting
                can be enabled if desired. Points for polygon objects can be entered directly or pasted from a NOTAM.
            </div>
        ),
        blockTargetInteraction: false,
        hideBackButton: true,
        skipBeacon: true
    },
    {
        target: '#drawing-objects-edit-window',
        placement: 'left',
        content: (
            <div>
                To edit an existing drawing object, right click anywhere on the object, and select Edit graphically. Objects can be dragged
                whole or vertext points can be dragged individually. This context menu will also allow the object to be deleted and
                properties dialog opened. Holding Analyzer will show a list of all flights within this object, sorted by altitude. Drawing
                objects can also be edited, deleted or exported from the Drawing Object list dialog.
            </div>
        ),
        blockTargetInteraction: false,
        skipBeacon: true
    },
    {
        target: '#drawing-objects-tools-btn',
        title: 'Holding Analyzer',
        content: (
            <div>
                Holding Analyzer allows you to view a list of all aircraft inside a drawing object, sorted by altitude. This can be accessed
                via the Drawing Object right click context menu.
            </div>
        ),
        placement: 'auto',
        hideBackButton: true,
        skipBeacon: true,
        blockTargetInteraction: false
    }
];

const otherTools: FeStep[] = [
    {
        target: '#nav-customer-admin',
        title: 'Customer admin tool',
        content: `The Customer Admin tool is now available for account maintenance, airline diversion manager settings and ACARS settings.`,
        skipBeacon: true,
        data: {
            requiresCustomerAdmin: true
        }
    },

    {
        target: '#weather-legend-btn',
        title: 'Weather legend',
        content: (
            <div>
                All weather layers currently selected in the View can be seen at a glance in the new on-map weather layers section.
                Individual layers can be shown and hidden, as well as removed from the View.
                <br />
                <br />
                <em>Click Weather layers icon.</em>
            </div>
        ),
        placement: 'top',
        skipBeacon: true,
        blockTargetInteraction: false
    },
    {
        target: '#nav-flight-list',
        title: 'Flight List',
        content: (
            <div>
                New flight list. All flights can be shown in tabular form. Fields can be customized, filtered and sorted in numerous ways.
                <br />
                <br />
                <em>Click Flight list icon.</em>
            </div>
        ),
        hideBackButton: true,
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#nav-airport-list',
        title: 'Airport List',
        content: (
            <div>
                New airport list. All airports can be shown in tabular form. Fields can be customized, filtered and sorted in numerous ways.
                <br />
                <br />
                <em>Click Airport list icon.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#nav-report',
        title: 'Reporting tool',
        content: (
            <div>
                The online Reporting Tool has been enhanced to provide easy access to tabular and graphical flight history details up to 18
                months in the past.
                <br />
                <br />
                <em>Click Reports icon.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#nav-system-hub',
        title: 'System hub',
        content: (
            <div>
                The Flight Explorer System Hub is the single, consolidated place to look for news, alerts, announcements, real-time feed
                status, current version and support/contact information. The System Hub icon will also show a yellow or red badge to
                indicate that an urgent message is available.
                <br />
                <br />
                <em>Click System Hub icon.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        data: {
            autoClick: true
        }
    },
    {
        target: '#tutorial-btn',
        title: 'Tutorials',
        content: (
            <div>
                Similar to this "What's new" tutorial, Flight Explorer provides additional tutorials covering other commonly used
                functionality.
                <br />
                <br />
                <em>Click Help/tutorial icon.</em>
            </div>
        ),
        placement: 'auto',
        skipBeacon: true,
        blockTargetInteraction: false,
        before: () => {
            (document.querySelector('#nav-home') as HTMLButtonElement)?.click();
            return delay(250);
        },
        data: {
            autoClick: true
        }
    },
    {
        target: '#map-0',
        title: 'Save map image...',
        content: (
            <div>Current map can be saved as an image file. This is accessed on the aircraft context menu and the toolbar User menu.</div>
        ),
        placement: 'bottom',
        skipBeacon: true,
        hideBackButton: true,
        blockTargetInteraction: false,
        data: {
            afterNavigate: `${ROUTES.SETTINGS}/${SETTINGS_ROUTES.externalLinkIntegration}`
        }
    },
    {
        skipScroll: true,
        target: '.settings-section-content',
        title: 'External Link Integration (ELI)',
        content: (
            <div>
                External links can be added to both aircraft and airport context menus. These links can automatically embed custom
                parameters to allow quick access to internal and external web based data sources from within Flight Explorer.
            </div>
        ),
        placement: 'bottom',
        skipBeacon: true,
        blockTargetInteraction: false,
        hideBackButton: true,
        data: {
            afterNavigate: `${ROUTES.SETTINGS}/${SETTINGS_ROUTES.aircraft.index}`
        }
    }
];

const whatsNew: FeStep[] = [
    {
        target: '#workspace-layout-toolbar-btn',
        title: 'Multimap',
        content: <div>Map layout button allows displaying up to 4 maps.</div>,
        skipBeacon: true
    },
    {
        target: '#toolbar-clocks',
        title: 'Time zone bar',
        content: (
            <div>
                New Time Zone bar allows you to set up to eight custom times and labels in a bar at the top of the map. Times can be
                configured in Settings/General/Application, and the bar can be shown/hidden via icon in the top tool bar.
            </div>
        )
    },
    {
        target: '#fleet-monitor .open-in-external-window-btn',
        title: 'Tear-off  windows',
        content: (
            <div>
                Fleet monitor, Airport monitor, Event list, Map legend, Flight list, Airport list and Route analyzer can be opened in a
                separate window. Window sizes and placements will be remembered for this account.
            </div>
        ),
        skipBeacon: true,
        before: ({ action }) => {
            if (action === ACTIONS.NEXT) {
                // Use store action
                (document.querySelector('#home-fleet-monitor:not(.active)') as HTMLElement)?.click();
                return delay(200);
            }
            return Promise.resolve();
        }
    },
    {
        target: '#flight-context-menu-destination-winds',
        title: 'Runway wind calculator',
        content: (
            <div>
                The new Runway Wind Calculator allows you to see the runway wind breakdown, both graphically and tabularly, using METAR,
                TAF, ITWS or custom wind values. It can be accessed in Airport Summary from the runway list, and also via plane and airport
                context menus.
            </div>
        ),
        after: ({ action }) => {
            if (action === ACTIONS.PREV) {
                EventBus.fire('tutorial-component', null);
                return Promise.resolve();
            }
            return Promise.resolve();
        },
        before: ({ action }) => {
            if (action === ACTIONS.NEXT) {
                EventBus.fire('tutorial-component', {
                    type: 'flight-context-menu',
                    elementId: '#map-0'
                });
                return delay(100);
            }
            return Promise.resolve();
        }
    },
    {
        target: '#flight-context-menu-fast-forward',
        title: 'Flight Fast Forward',
        blockTargetInteraction: true,
        content: (
            <div>
                Flight Fast Forward allows you to animate a flight forward in time, fully synchronized with weather to view any future
                impacts. It is accessible via the plane context menu.
            </div>
        ),
        after: ({ action }) => {
            if (action === ACTIONS.NEXT) {
                EventBus.fire('tutorial-component', null);
                return Promise.resolve();
            }
            return Promise.resolve();
        },
        before: ({ action }) => {
            if (action === ACTIONS.PREV) {
                EventBus.fire('tutorial-component', {
                    type: 'flight-context-menu',
                    elementId: '#map-0'
                });
                return delay(100);
            }
            return Promise.resolve();
        }
    },
    {
        target: '#flight-summary-breached-thresholds-header',
        title: 'Flight details cascade breached thresholds',
        before: () => {
            store.dispatch(setTutorialSelectedFlight());
            return delay(100);
        },
        content: (
            <div>
                In Flight Details, for any origin, destination or alternate breached thresholds, you can now left click the status and
                quickly see the corresponding airport breached thresholds driving it.
            </div>
        )
    },
    {
        target: '#flight-details-flyout .flight-header-destination',
        title: 'Airport Taxi times',
        content: <div>Airport Summary now shows a taxi time graph for select US airports. </div>
    },
    {
        target: '#flight-details-flyout .flight-header-destination',
        title: 'Airport Traffic Graph',
        content: <div>Airport Summary now shows an enhanced forecast airport traffic graph. </div>
    },
    {
        target: '#layers-aircraft-route-flown-setting',
        title: 'Flown point type display',
        before: () => {
            EventBus.fire('open-flyout', {
                type: HomeFlyoutTypes.layers,
                sub: {
                    type: LayersNavState.AircraftSettings,
                    sub: LayersAircraftNavState.aircraft
                }
            });
            return delay(250);
        },
        content: (
            <div>
                Points in the flown history plotted on the map can now be colorized based on their source. This can be configured in the
                Plane layer settings menu.
            </div>
        )
    },
    {
        target: '#flyout-layers-custom',
        title: 'GEO Json',
        before: () => {
            EventBus.fire('open-flyout', { type: HomeFlyoutTypes.layers });
            return delay(350);
        },
        content: (
            <div>
                A new GeoJSON layer type is now available, which allows you to create custom GeoJSON overlays to integrate into the Flight
                Explorer map. This is accessible via the Layers menu.
            </div>
        )
    },
    {
        target: '#command-center',
        title: 'Keyboard shortcuts',
        blockTargetInteraction: true,
        before: () => {
            EventBus.fire('open-command-center', null);
            return delay(100);
        },
        content: <div>Keyboard shortcuts are now defined for several features. Others will be added in future versions. </div>
    },
    {
        target: '#map-0',
        title: 'Drag zoom',
        content: (
            <div>
                Left-click drag zoom is now available, in addition to scroll wheel zoom. Simply hold down the shift key, press the left
                mouse button, drag a rectangle to zoom in on and release the mouse button.
            </div>
        )
    },
    {
        target: '#map-0',
        title: 'Playback jump to time',
        content: <div>Playback now has the option to jump to a specific time in the recording. </div>
    }
];

const tutorials = {
    createUdView,
    myFleetAndFilter,
    weather,
    layers,
    flightDetails,
    home,
    otherTools,
    monitoringAlerting,
    routeAnalyzer,
    drawingObjects,
    whatsNew
} as const;

export type TutorialType = keyof typeof tutorials;

export default tutorials;

function delayFunc(ms: number) {
    return () => delay(ms);
}

function delay(ms: number) {
    return new Promise<void>((resolve) => {
        self.setTimeout(() => {
            resolve();
        }, ms);
    });
}
